import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import {
  BookHeldSlotInput,
  HoldSlotInput,
  HoldSlotResult,
  SLOT_HOLD_IDENTIFIER_SYSTEM,
} from '@bariendo/lib/slot-holds';
import { FindAvailabilityRequest, FindAvailabilityWindow } from '@bariendo/lib/scheduling';
import { PacIntroCallSlotSelection } from '@bariendo/lib/patient-access-note';
import { PN_CONSULTATION_CODE } from '@bariendo/lib/constants';
import { PatientTimeZone, TIME_ZONE_LABELS, patientTimeZone } from '@bariendo/lib/patient-time-zone';
import { MedplumClient, getReferenceString } from '@medplum/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useState } from 'react';

// Extended here as well as in `main.tsx`: these read `.tz()` on the patient's
// zone, and a unit test rendering them does not boot the app. `extend` is
// idempotent.
dayjs.extend(utc);
dayjs.extend(timezone);

/** A consultation the grid can offer, held and booked through the same bots. */
export interface ConsultType {
  appointmentType: string;
  /** Mirrors the ActivityDefinition's timingDuration; slots are offered on this grid. */
  durationMinutes: number;
}

/** The NP consultation — what the legacy scheduled intro call books. */
export const NP_CONSULT: ConsultType = { appointmentType: 'app-initial-consultation-visit', durationMinutes: 45 };

/** The PN consultation — what the PAC intro call books (BAR-2775). */
export const PN_CONSULT: ConsultType = { appointmentType: PN_CONSULTATION_CODE, durationMinutes: 30 };

/**
 * How many days forward the rep can step, today included.
 *
 * The script still leads with "today or tomorrow", but a caller who can't do
 * either needs a real date on this call, not a callback (BAR-2841).
 */
export const BOOKABLE_DAYS = 14;

export interface ConsultSlot {
  start: string;
  end: string;
  practitionerReference?: string;
  practitionerName?: string;
}

/**
 * Identity of a bookable slot.
 *
 * `start` alone is not it: several providers are routinely free at the same time,
 * so a grid keyed on `start` renders duplicate React keys, highlights every
 * provider at that time together, and reads a tap on a second provider as a
 * re-tap of the first — which released the hold instead of switching to it, so
 * the rep could never settle on a selection and nothing was ever booked.
 */
export function consultSlotKey(slot: Pick<ConsultSlot, 'start' | 'practitionerReference'>): string {
  return `${slot.start}|${slot.practitionerReference ?? ''}`;
}

/**
 * Cuts availability windows into bookable starts.
 *
 * The bots slice server-side when a hold is taken; this is the client-side view
 * of the same grid so the rep sees discrete times rather than ranges. The hold
 * remains the authority — a slot shown here can still lose a race, which is why
 * `hold` surfaces the failure instead of assuming success.
 */
export function sliceWindows(windows: FindAvailabilityWindow[], durationMinutes: number, now: Dayjs): ConsultSlot[] {
  const slots: ConsultSlot[] = [];

  for (const window of windows) {
    let cursor = dayjs(window.startTime);
    const windowEnd = dayjs(window.endTime);
    if (!cursor.isValid() || !windowEnd.isValid()) continue;

    while (cursor.add(durationMinutes, 'minute').isBefore(windowEnd.add(1, 'second'))) {
      const end = cursor.add(durationMinutes, 'minute');
      // A slot that has already started cannot be offered on this call.
      if (cursor.isAfter(now)) {
        slots.push({
          start: cursor.toISOString(),
          end: end.toISOString(),
          ...(window.resourceId ? { practitionerReference: window.resourceId } : {}),
        });
      }
      cursor = end;
    }
  }

  return slots.sort((a, b) => a.start.localeCompare(b.start));
}

async function resolvePractitionerNames(medplum: MedplumClient, slots: ConsultSlot[]): Promise<ConsultSlot[]> {
  const references = [...new Set(slots.map((slot) => slot.practitionerReference).filter(Boolean))] as string[];
  if (references.length === 0) return slots;

  const names = new Map<string, string>();
  await Promise.all(
    references.map(async (reference) => {
      try {
        const practitioner = await medplum.readReference<Practitioner>({ reference });
        const name = practitioner.name?.[0];
        const display = [name?.given?.join(' '), name?.family].filter(Boolean).join(' ');
        if (display) names.set(reference, display);
      } catch {
        // A missing name degrades the slot label; it must not drop the slot.
      }
    })
  );

  return slots.map((slot) => ({
    ...slot,
    ...(slot.practitionerReference && names.has(slot.practitionerReference)
      ? { practitionerName: names.get(slot.practitionerReference) }
      : {}),
  }));
}

/**
 * Live slot grid for the "Book the consultation" beat.
 *
 * Reuses the generic scheduling bots rather than anything CI-specific:
 * `find-availability` for the grid, `hold-slot` to reserve the chosen time
 * while the rep keeps talking, `book-held-slot` to confirm it. `consult` picks
 * the appointment type those bots are asked for and the grid it is cut on; it
 * defaults to the NP consultation the legacy scheduled card books.
 *
 * **Optionally, the day is the patient's day rather than the rep's.** `Today`
 * for a rep in Miami ends three hours before it does for a caller in Portland,
 * so a window cut on the browser's clock offers a Pacific caller times that are
 * already gone and hides the evening ones they could actually take.
 *
 * One flag governs both halves on purpose. The window this hook asks for and the
 * zone the grid renders in have to be the same zone: a card that fetched the
 * patient's day and labelled the rep's would show a Hawaii day under an Eastern
 * heading. `inPatientTimeZone` off means neither — the browser's clock for both,
 * exactly as before BAR-2612 — which is what the legacy scheduled card wants.
 */
export function useNpConsultSlots(
  patient: Patient | undefined,
  enabled: boolean,
  { inPatientTimeZone = false, consult = NP_CONSULT }: { inPatientTimeZone?: boolean; consult?: ConsultType } = {}
) {
  const medplum = useMedplum();
  const derivedTimeZone = useMemo(
    () => (inPatientTimeZone ? patientTimeZone(patient) : undefined),
    [inPatientTimeZone, patient]
  );
  // The ZIP is the starting point, not the last word: a chart ZIP goes stale, a
  // caller has moved, or the patient says outright which zone they are in. The
  // rep overrides it on the grid and everything — the window, the day label, the
  // times — follows.
  const [chosenTimeZone, setChosenTimeZone] = useState<string | undefined>();
  const timeZone: PatientTimeZone | undefined = useMemo(() => {
    if (!derivedTimeZone) return undefined;
    if (!chosenTimeZone) return derivedTimeZone;
    // Re-derived, not spread: an object carrying the chosen id under the derived
    // zone's name would print "Pacific (CDT)" to the first caller that reads it.
    return { timeZone: chosenTimeZone, label: TIME_ZONE_LABELS[chosenTimeZone] ?? chosenTimeZone, fromZip: false };
  }, [chosenTimeZone, derivedTimeZone]);
  const [dayOffset, setDayOffset] = useState(0);
  const [slots, setSlots] = useState<ConsultSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [isBooking, setIsBooking] = useState(false);

  useEffect(() => {
    // Only once the rep opens the booking beat. Most calls never reach it, and
    // availability is stale by the time they would.
    if (!enabled) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const day = (timeZone ? dayjs().tz(timeZone.timeZone) : dayjs()).add(dayOffset, 'day');
        const request: FindAvailabilityRequest = {
          serviceTypes: [consult.appointmentType],
          startTime: day.startOf('day').toISOString(),
          endTime: day.endOf('day').toISOString(),
        };

        const windows = (await fetchBot(medplum, 'find-availability', request)) as FindAvailabilityWindow[];
        // A slot the grid cannot attribute to a provider cannot be held for one: the
        // hold goes out unowned and hold-slot 404s with "Nobody is available".
        const attributed = (windows ?? []).filter((window) => window.resourceId);
        const sliced = sliceWindows(attributed, consult.durationMinutes, dayjs());
        const withNames = await resolvePractitionerNames(medplum, sliced);
        if (!cancelled) setSlots(withNames);
      } catch (error) {
        if (!cancelled) {
          setSlots([]);
          alert.error(`Couldn't load consultation times: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [medplum, dayOffset, enabled, timeZone?.timeZone, consult.appointmentType, consult.durationMinutes]);

  /**
   * Reserve the slot while the rep finishes the call. Returns undefined when the
   * hold fails — usually because someone else took the time first.
   */
  const hold = useCallback(
    async (slot: ConsultSlot): Promise<PacIntroCallSlotSelection | undefined> => {
      try {
        const input: HoldSlotInput = {
          appointmentType: consult.appointmentType,
          startTime: slot.start,
          // The grid offers a provider per button, so the hold has to be for that
          // provider. Left off, hold-slot gives the time to whoever is least booked
          // and the card records someone who was never held.
          ...(slot.practitionerReference ? { practitionerReference: slot.practitionerReference } : {}),
        };
        const result = (await fetchBot(medplum, 'hold-slot', input)) as HoldSlotResult;
        if (!result?.holdId) {
          alert.error('That time was just taken. Pick another.');
          return undefined;
        }

        // Whose time was actually held is the server's answer, not the button's.
        const practitionerReference = result.practitionerReference ?? slot.practitionerReference ?? '';
        const heldWhoWasAsked = practitionerReference === slot.practitionerReference;

        return {
          start: result.startTime ?? slot.start,
          end: result.endTime ?? slot.end,
          practitionerReference,
          ...(heldWhoWasAsked && slot.practitionerName ? { practitionerName: slot.practitionerName } : {}),
          slotId: result.holdId,
        };
      } catch (error) {
        alert.error(`Couldn't hold that time: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return undefined;
      }
    },
    [medplum, consult.appointmentType]
  );

  /**
   * Give a hold back when the rep changes their mind.
   *
   * Without this, every slot they audition stays reserved until
   * `release-expired-holds` sweeps it — a rep clicking through four times
   * blocks four consultation slots for other leads. The sweeper remains the
   * backstop, so a failed release is logged-and-ignored rather than surfaced:
   * it costs availability for the TTL, and interrupting a live call over it
   * would be worse.
   *
   * Deleting the Slot is what the sweeper does, and the CI access policy grants
   * Slot write for exactly this kind of scheduling work.
   */
  const release = useCallback(
    async (selection: PacIntroCallSlotSelection | undefined): Promise<void> => {
      if (!selection?.slotId) return;

      try {
        const held = await medplum.searchResources('Slot', {
          identifier: `${SLOT_HOLD_IDENTIFIER_SYSTEM}|${selection.slotId}`,
        });
        await Promise.all(held.filter((slot) => slot.id).map((slot) => medplum.deleteResource('Slot', slot.id!)));
      } catch {
        // Intentionally swallowed — release-expired-holds will reclaim it.
      }
    },
    [medplum]
  );

  /** Confirm the held slot as a real appointment. */
  const book = useCallback(
    async (selection: PacIntroCallSlotSelection): Promise<boolean> => {
      const patientId = patient ? getReferenceString(patient) : undefined;
      if (!selection.slotId || !patientId) return false;

      setIsBooking(true);
      try {
        const input: BookHeldSlotInput = {
          holdId: selection.slotId,
          patientId,
          encounterClass: 'VR',
        };
        await fetchBot(medplum, 'book-held-slot', input);
        return true;
      } catch (error) {
        alert.error(`Couldn't book the consultation: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return false;
      } finally {
        setIsBooking(false);
      }
    },
    [medplum, patient]
  );

  return {
    slots,
    loading,
    timeZone,
    /** What the ZIP said, so the grid can show whether the rep has moved off it. */
    derivedTimeZone,
    setTimeZone: setChosenTimeZone,
    dayOffset,
    setDayOffset,
    hold,
    release,
    book,
    isBooking,
    maxDayOffset: BOOKABLE_DAYS - 1,
  };
}
